import { AsyncPipe, CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateLoader } from '@cfp/core';
import { TranslateConstructionPeriodPipe } from '@cfp/shared/ui/construction-period';
import { MeasurementService } from '@cfp/shared/ui/measurement';
import { Alert, CfpTranslateModule, Icon, Link } from '@cfp/ui';
import {
  AuditCategoryDTO,
  BuildingChangeLogItemDTO,
  BuildingDetailsDTO,
  BuildingSystemChangeLogItemDTO
} from 'csr-asset-app';
import { UnitsAreaDTO } from 'csr-login-app';
import { catchError, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuditTrailUnitPipe } from '../pipes';

@Component({
  selector: 'cfp-audit-trail-item',
  templateUrl: 'audit-trail-item.html',
  providers: [{ provide: DecimalPipe, useClass: DecimalPipe }],
  imports: [
    Alert,
    Link,
    Icon,
    CfpTranslateModule,
    DatePipe,
    AuditTrailUnitPipe,
    TranslateConstructionPeriodPipe,
    CurrencyPipe,
    AsyncPipe
  ]
})
export class AuditTrailItem<T extends BuildingChangeLogItemDTO | BuildingSystemChangeLogItemDTO> {
  public readonly logItem = input.required<T>();
  public readonly building = input.required<BuildingDetailsDTO>();
  public readonly showPriceField = input<boolean>(false);
  public readonly itemClicked = output<BuildingSystemChangeLogItemDTO[]>();

  protected readonly AuditCategoryDTO = AuditCategoryDTO;
  protected readonly measurementService = inject(MeasurementService);
  private readonly translateLoaderService = inject(TranslateLoader);

  public showSystemChanges(systemChanges: BuildingSystemChangeLogItemDTO[]): void {
    this.itemClicked.emit(systemChanges);
  }

  private readonly isCrremTranslationLoaded = toSignal(
    this.translateLoaderService.loadTranslation('crrem').pipe(
      map(() => true),
      catchError(() => of(true))
    )
  );

  private readonly isWeiiTranslationLoaded = toSignal(
    this.translateLoaderService.loadTranslation('weii').pipe(
      map(() => true),
      catchError(() => of(true))
    )
  );

  protected areAllTranslationsLoaded = computed(
    () => this.isCrremTranslationLoaded() && this.isWeiiTranslationLoaded()
  );

  protected readonly buildingChangeLogItem = computed(() => {
    if ('userName' in this.logItem()) {
      return this.logItem() as BuildingChangeLogItemDTO;
    }
  });

  protected get unitsArea(): UnitsAreaDTO {
    return this.measurementService.userUnitsArea;
  }

  protected extractYearPart(input: string): string | null {
    const match = input.match(/\([^)]*\)$/);
    return match ? match[0] : null;
  }

  protected extractLabelPart(input: string): string {
    return input.replace(/\s*\([^)]*\)$/, '').trim();
  }
}
